<script setup lang="ts">
import Category from '@/api/Category'
import Product from '@/api/Product'
import AppImage from '@/components/common/AppImage.vue'
import ProductCard from '@/components/common/ProductCard.vue'
import Error404 from '@/components/common/errors/404Error.vue'
import ProductCardSkeleton from '@/components/common/skeleton/ProductCardSkeleton.vue'
import { useScreenSize } from '@/composables/screen-size'
import {
  AppPagination,
  AppSection,
  AppSkeleton,
  HeadingSkeleton,
  ParagraphSkeleton,
  useToast
} from 'lilasia-ui'
import { onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'

const props = defineProps<{
  slug: string
}>()

const { isMobile } = useScreenSize()
const { toastError } = useToast()
const route = useRoute()

const category = ref<Category | null>(null)
const products = ref<Product[]>([])
const metadata = ref<Meta>()
const loading = ref(false)
const notFound = ref(false)

watch(
  () => route.query,
  (newQuery) => {
    if (newQuery) {
      fetchProducts(newQuery)
    }
  }
)

onMounted(() => {
  fetchCategory()
  fetchProducts(route.query)
})

const fetchCategory = async () => {
  loading.value = true

  try {
    category.value = await Category.show<Category>(props.slug)
  } catch (error) {
    const fetchError = error as FetchError
    if (404 === fetchError.code) {
      notFound.value = true
    }
  } finally {
    loading.value = false
  }
}

const fetchProducts = async (query = {}) => {
  loading.value = true

  try {
    const { data, meta } = await Product.browse<Paginate<Product>>({
      ...query,
      categories: props.slug
    })

    products.value = data
    metadata.value = meta
  } catch (error) {
    const fetchError = error as FetchError
    toastError(fetchError.message)
  } finally {
    loading.value = false
  }
}
</script>

<template>
  <div>
    <template v-if="category">
      <AppSection
        no-gap
        fluid
      >
        <div class="flex flex-col gap-8 md:gap-24">
          <AppImage
            v-if="category.image_url"
            :src="category.image_url"
            :alt="category.name"
            :width="isMobile ? 480 : 1440"
            :height="isMobile ? 200 : 480"
            :rounded="0"
            class="h-[200px] w-full object-cover md:h-[480px]"
          />

          <div class="container mx-auto flex flex-col gap-8">
            <div class="text-24 font-700 leading-32">{{ category.name }}</div>
            <!-- eslint-disable vue/no-v-html -->
            <div
              class="text-16 leading-24 text-black-80"
              v-html="category.description"
            ></div>
          </div>
        </div>
      </AppSection>

      <AppSection
        no-gap
        class="mt-24"
      >
        <div v-if="products.length">
          <div class="grid grid-cols-2 gap-16 md:grid-cols-4 lg:grid-cols-5">
            <ProductCard
              v-for="(product, index) in products"
              :key="index"
              :product="product"
            />
          </div>
        </div>
      </AppSection>

      <AppSection v-if="metadata && metadata?.last_page > 1">
        <div class="flex justify-center">
          <AppPagination
            :meta="metadata"
            :hide-page-numbers="isMobile"
          />
        </div>
      </AppSection>
    </template>

    <template v-else-if="loading">
      <AppSection
        no-gap
        fluid
      >
        <div class="flex flex-col gap-8 md:gap-24">
          <AppSkeleton class="h-[300px] w-full rounded-[0] md:h-[480px]" />
          <div class="container mx-auto flex flex-col gap-16">
            <HeadingSkeleton class="w-1/2" />
            <ParagraphSkeleton :rows="2" />
          </div>
        </div>
      </AppSection>

      <AppSection
        no-gap
        class="mt-24"
      >
        <div class="grid grid-cols-2 gap-16 md:grid-cols-4 lg:grid-cols-5">
          <ProductCardSkeleton
            v-for="n in 5"
            :key="n"
          />
        </div>
      </AppSection>
    </template>

    <Error404 v-else-if="notFound" />
  </div>
</template>
